import { useDimensions } from "@repo/core/hooks/useDimensions";
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Text } from "@repo/core/components/core/Text";
import { convertDataRows } from "@repo/core/components/core/DataTable/utils";
import { TableConvertedData } from "@repo/core/components/core/DataTable/types";
import { exportTanstackTableToCSV } from "@repo/core/utils/csv.utils";
import { useMemo, useRef, useState } from "react";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import { ReportType, ReportTypeEnum } from "../../types";
import { format } from "date-fns";
import { ROUTES } from "@repo/core/routes";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useDeviceType } from "@repo/core/hooks/useDeviceType";
import { useReportsFilters } from "@repo/core/hooks/useReportsFilters";
import { ReportsResource } from "@repo/core/services/resources/reports";
import { newApInstance } from "@repo/core/services/instances/new-api";
import { GeneralReportTypes } from "@repo/core/types/reports";
import { useQuery } from "@tanstack/react-query";

const reportsResource = new ReportsResource(newApInstance);

export const GeneralReportController = () => {
  const { isMobile } = useDimensions();
  const { isMobile: isMobileDevice } = useDeviceType();
  const { push } = useNavigation();

  const {
    selectedAttendant,
    selectedProduct,
    selectedStore,
    filterStartDate,
    filterEndDate,
    onChangeStartDate,
    onChangeEndDate
  } = useReportsFilters();

  const [selectedReportType, setSelectedReportType] = useState<string>(
    ReportTypeEnum.PROPOSALS
  );

  const filterModalRef = useRef<IModalRefProps>(null);

  const { data: generalReportsResponse, isFetching: isFetchingGeneralReports } =
    useQuery({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: [
        "generalReports",
        filterStartDate,
        filterEndDate,
        selectedStore,
        selectedProduct,
        selectedAttendant,
        selectedReportType
      ],
      queryFn: () =>
        reportsResource.getGeneralReports({
          startDate: format(filterStartDate, "yyyy-MM-dd"),
          endDate: format(filterEndDate, "yyyy-MM-dd"),
          store: selectedStore,
          product: selectedProduct,
          attendant: selectedAttendant,
          type: selectedReportType as GeneralReportTypes
        })
    });

  const onSelectReportType = (reportType: ReportTypeEnum) => {
    setSelectedReportType(reportType);
  };

  const columnHelper = createColumnHelper();

  const reportTypes: ReportType[] = [
    {
      title: "Propostas",
      type: ReportTypeEnum.PROPOSALS
    },
    {
      title: "Vendas",
      type: ReportTypeEnum.SALES
    },
    {
      title: "Mais informações",
      type: ReportTypeEnum.MORE_INFO
    }
  ];

  const headerColumns = generalReportsResponse?.data?.table.headers.map(
    (header) => {
      return columnHelper.accessor(header.key, {
        header: () => (
          <Text
            fontSize="$xs"
            color={header?.styles?.text?.color || "$textLight700"}
            fontWeight={header?.styles?.text?.fontWeight || 600}
          >
            {header.label}
          </Text>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        meta: header?.styles
      });
    }
  );

  const { tableData, hasReportData } = useMemo(() => {
    const tableData = convertDataRows(
      generalReportsResponse?.data?.table.headers,
      generalReportsResponse?.data?.table.rows
    );

    const hasReportData =
      Array.isArray(generalReportsResponse?.data?.table.rows) &&
      generalReportsResponse.data.table.rows.length > 0;

    return {
      tableData,
      hasReportData
    };
  }, [generalReportsResponse?.data]);

  const table = useReactTable({
    data: tableData,
    columns: headerColumns as AccessorKeyColumnDef<TableConvertedData>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel()
  });

  const topRightText = useMemo(() => {
    return `Atualizado em ${format(new Date(), "dd/MM")} às ${format(new Date(), "HH:mm:ss")}`;
  }, []);

  const handleExportTable = () => {
    exportTanstackTableToCSV(
      "Relatório_Atendentes",
      generalReportsResponse?.data.table.headers,
      table
    );
  };

  const handleSelectFilters = () => {
    if (isMobileDevice) {
      push(ROUTES.reportSelectFilters);
    } else {
      if (filterModalRef.current) {
        filterModalRef.current.open();
      }
    }
  };

  return {
    isMobile,
    onChangeStartDate,
    onChangeEndDate,
    table,
    handleExportTable,
    filterModalRef,
    reportTypes,
    selectedReportType,
    onSelectReportType,
    topRightText,
    filterStartDate,
    filterEndDate,
    hasReportData,
    handleSelectFilters,
    isFetchingGeneralReports
  };
};
